import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

import {
  ExternalLinkIcon,
  FileIcon,
  Loader2Icon,
  XCircleIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import { Button } from '@/components/ui/catalyst/button';
import { useGetBlobDetailQuery } from '@/services/api/blobService';
import type { Blob } from '@/services/types/blob-types';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface ExhibitDocumentPreviewProps {
  matterId: string;
  blob: Blob;
}

const PREVIEW_WIDTH = 580;
const MAX_PREVIEW_PAGES = 3;

/**
 * Compact PDF preview for exhibit documents before adding them.
 * Shows the first 1-2 pages to help users verify they selected the correct document.
 */
export const ExhibitDocumentPreview = ({
  matterId,
  blob,
}: ExhibitDocumentPreviewProps) => {
  const {
    data: blobDetail,
    isLoading: isDetailLoading,
    error: detailError,
  } = useGetBlobDetailQuery(
    { matterId, blobId: blob.id },
    { skip: !blob.id || !matterId },
  );

  const [numPages, setNumPages] = useState<number | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(true);

  // Reset state when blob changes
  useEffect(() => {
    setNumPages(null);
    setPdfError(null);
    setIsLoadingPdf(true);
  }, [blob.id]);

  const handleDocumentLoadSuccess = ({
    numPages: pageCount,
  }: {
    numPages: number;
  }) => {
    setNumPages(pageCount);
    setIsLoadingPdf(false);
    setPdfError(null);
  };

  const handleDocumentLoadError = () => {
    setPdfError('Failed to load PDF preview');
    setIsLoadingPdf(false);
  };

  const handleOpenInNewTab = () => {
    if (blobDetail?.download_url || blob.download_url) {
      window.open(blobDetail?.download_url || blob.download_url, '_blank');
    }
  };

  // File metadata display
  const fileSize = blob.file_size
    ? formatFileSize(blob.file_size)
    : 'Unknown size';

  // Loading state
  if (isDetailLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-gray-500">
        <Loader2Icon className="h-6 w-6 animate-spin mb-2" />
        <span className="text-xs">Loading document details...</span>
      </div>
    );
  }

  // Error fetching blob details
  if (detailError) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-gray-500">
        <XCircleIcon className="h-6 w-6 mb-2 text-red-400" />
        <span className="text-xs">Failed to load document</span>
      </div>
    );
  }

  const downloadUrl = blobDetail?.download_url || blob.download_url;

  // Check if it's a PDF or image that can be previewed
  const isPdf =
    blob.file_mime_type === 'application/pdf' ||
    blob.name?.toLowerCase().endsWith('.pdf');

  const isImage =
    (blob.file_mime_type?.startsWith('image/') ?? false) ||
    /\.(jpe?g|png|gif|bmp|tiff?|webp|heic)$/i.test(blob.name ?? '');

  if ((!isPdf && !isImage) || !downloadUrl) {
    return (
      <div className="flex flex-col items-center justify-center py-8 border border-dashed rounded-lg bg-gray-50/50">
        <div className="rounded-full bg-gray-100 p-3 mb-3">
          <FileIcon className="h-6 w-6 text-gray-400" />
        </div>
        <p className="text-xs font-medium text-gray-700 mb-1">{blob.name}</p>
        <p className="text-xs text-gray-500">{fileSize}</p>
        {downloadUrl && (
          <Button
            plain
            onClick={handleOpenInNewTab}
            className="mt-3 text-xs text-blue-600 hover:text-blue-700"
          >
            <ExternalLinkIcon className="h-3 w-3 mr-1" />
            Open in new tab
          </Button>
        )}
      </div>
    );
  }

  if (isImage && downloadUrl) {
    return (
      <div className="flex flex-col border rounded-lg bg-gray-50/50 overflow-hidden">
        {/* Preview header */}
        <div className="flex items-center justify-between px-3 py-2 border-b bg-white">
          <div className="flex flex-col min-w-0 flex-1">
            <p
              className="text-xs font-medium text-gray-700 truncate"
              title={blob.name}
            >
              {blob.name}
            </p>
            <p className="text-[10px] text-gray-500">{fileSize}</p>
          </div>
          <Button
            plain
            onClick={handleOpenInNewTab}
            className="text-xs text-gray-500 hover:text-gray-700 ml-2 shrink-0"
            title="Open in new tab"
          >
            <ExternalLinkIcon className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Image Preview */}
        <div className="relative min-h-[400px] max-h-[600px] overflow-y-auto bg-gray-100 p-3">
          <img
            src={downloadUrl}
            alt={blob.name}
            className="mx-auto max-w-full h-auto rounded shadow-sm bg-white"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col border rounded-lg bg-gray-50/50 overflow-hidden">
      {/* Preview header */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-white">
        <div className="flex flex-col min-w-0 flex-1">
          <p
            className="text-xs font-medium text-gray-700 truncate"
            title={blob.name}
          >
            {blob.name}
          </p>
          <p className="text-[10px] text-gray-500">
            {fileSize}
            {numPages && ` • ${numPages} page${numPages > 1 ? 's' : ''}`}
          </p>
        </div>
        <Button
          plain
          onClick={handleOpenInNewTab}
          className="text-xs text-gray-500 hover:text-gray-700 ml-2 shrink-0"
          title="Open in new tab"
        >
          <ExternalLinkIcon className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* PDF Preview */}
      <div className="relative min-h-[400px] max-h-[600px] overflow-y-auto bg-gray-100">
        {isLoadingPdf && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80 z-10">
            <div className="flex flex-col items-center">
              <Loader2Icon className="h-5 w-5 animate-spin text-blue-500 mb-2" />
              <span className="text-xs text-gray-500">Loading preview...</span>
            </div>
          </div>
        )}

        {pdfError && (
          <div className="flex flex-col items-center justify-center py-8">
            <XCircleIcon className="h-6 w-6 text-red-400 mb-2" />
            <p className="text-xs text-gray-600 mb-2">{pdfError}</p>
            <Button
              plain
              onClick={handleOpenInNewTab}
              className="text-xs text-blue-600 hover:text-blue-700"
            >
              <ExternalLinkIcon className="h-3 w-3 mr-1" />
              Open in new tab
            </Button>
          </div>
        )}

        {!pdfError && downloadUrl && (
          <Document
            file={downloadUrl}
            onLoadSuccess={handleDocumentLoadSuccess}
            onLoadError={handleDocumentLoadError}
            loading={null}
            className="flex flex-col items-center py-2"
          >
            {numPages &&
              Array.from(
                { length: Math.min(numPages, MAX_PREVIEW_PAGES) },
                (_, index) => (
                  <Page
                    key={`preview-page-${index + 1}`}
                    pageNumber={index + 1}
                    width={PREVIEW_WIDTH}
                    className="mb-2 shadow-sm"
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                ),
              )}
            {numPages && numPages > MAX_PREVIEW_PAGES && (
              <p className="text-[10px] text-gray-500 pb-2">
                Showing {MAX_PREVIEW_PAGES} of {numPages} pages
              </p>
            )}
          </Document>
        )}
      </div>
    </div>
  );
};

/**
 * Format file size in human-readable form
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${parseFloat((bytes / 1024 ** i).toFixed(1))} ${units[i]}`;
}
